import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { SearchResults } from '../lib/types';
import {
  CHANGE_STATUS_LABELS,
  INCIDENT_STATUS_LABELS,
  PROBLEM_STATUS_LABELS,
} from '../lib/types';
import { KindBadge, PriorityBadge, StatusDot } from '../components/Badges';

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [results, setResults] = useState<SearchResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState(query);

  useEffect(() => {
    setDraft(query);
    if (query.trim().length < 2) {
      setResults(null);
      return;
    }

    setResults(null);
    setError(null);
    api
      .get<SearchResults>(`/search?q=${encodeURIComponent(query)}`)
      .then(setResults)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not search'));
  }, [query]);

  const total = results
    ? results.companies.length +
      results.projects.length +
      results.tasks.length +
      results.incidents.length +
      results.problems.length +
      results.changes.length
    : 0;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Search</div>
          <h1>{query ? `“${query}”` : 'Search'}</h1>
          <p className="subtitle">
            Tasks, incidents, problems and changes by reference or wording, and projects and
            companies by name. Only what you can already open — search is never a way round
            the access rules.
          </p>
        </div>
      </div>

      <form
        className="card card-pad mb-2"
        onSubmit={(event) => {
          event.preventDefault();
          setParams(draft.trim().length >= 2 ? { q: draft.trim() } : {});
        }}
      >
        <div className="row" style={{ gap: 9 }}>
          <input
            type="search"
            value={draft}
            autoFocus
            onChange={(event) => setDraft(event.target.value)}
            placeholder="GOLD-PORTAL-9, PORTAL-CHG-18, or statement font"
          />
          <button type="submit" className="btn btn-primary" disabled={draft.trim().length < 2}>
            Search
          </button>
        </div>
      </form>

      {error && <div className="callout">{error}</div>}

      {query.trim().length < 2 ? (
        <div className="card">
          <div className="empty">
            <h3>What are you looking for?</h3>
            <p>Two characters is enough to start. Press / anywhere to jump to the search box.</p>
          </div>
        </div>
      ) : !results ? (
        <div className="empty">Searching…</div>
      ) : total === 0 ? (
        <div className="card">
          <div className="empty">
            <h3>Nothing matched “{query}”</h3>
            <p>Try part of a reference, a word from a title, or a project name.</p>
          </div>
        </div>
      ) : (
        <div className="stack">
          {results.tasks.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h2>Tasks</h2>
                <span className="dim" style={{ fontSize: 12 }}>
                  {results.tasks.length}
                </span>
              </div>
              <div>
                {results.tasks.map((task) => (
                  <Link
                    key={task.id}
                    to={`/projects/${task.project.id}/board?task=${task.id}`}
                    className={task.closed || task.done ? 'result-row is-done' : 'result-row'}
                  >
                    <div className="row wrap" style={{ gap: 8 }}>
                      <span className="key">{task.key}</span>
                      <span className="grow" style={{ minWidth: 0 }}>
                        <Highlight text={task.title} query={query} />
                      </span>
                      {task.status && <StatusDot color={task.status.color} label={task.status.label} />}
                      {task.closed && <span className="badge">closed</span>}
                    </div>
                    <div className="row wrap dim" style={{ gap: 10, fontSize: 11, marginTop: 3 }}>
                      <span>
                        {task.companyName} · {task.project.name}
                      </span>
                      <PriorityBadge priority={task.priority} />
                      {task.assigneeNames && <span>{task.assigneeNames}</span>}
                      {task.dueDate && <span className="mono">due {task.dueDate}</span>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {results.incidents.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h2>Incidents</h2>
                <span className="dim" style={{ fontSize: 12 }}>
                  {results.incidents.length}
                </span>
              </div>
              <div>
                {results.incidents.map((incident) => (
                  <Link
                    key={incident.id}
                    to={`/companies/${incident.companyId}/incidents`}
                    className={
                      incident.status === 'closed' || incident.status === 'resolved'
                        ? 'result-row is-done'
                        : 'result-row'
                    }
                  >
                    <div className="row wrap" style={{ gap: 8 }}>
                      <span className="key">{incident.reference}</span>
                      <span className="grow" style={{ minWidth: 0 }}>
                        <Highlight text={incident.title} query={query} />
                      </span>
                      <span className="badge">{INCIDENT_STATUS_LABELS[incident.status]}</span>
                    </div>
                    <div className="row wrap dim" style={{ gap: 10, fontSize: 11, marginTop: 3 }}>
                      <span>{incident.companyName}</span>
                      {incident.projectCode && <span className="key">{incident.projectCode}</span>}
                      <PriorityBadge priority={incident.priority} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {results.problems.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h2>Problems</h2>
                <span className="dim" style={{ fontSize: 12 }}>
                  {results.problems.length}
                </span>
              </div>
              <div>
                {results.problems.map((problem) => (
                  <Link
                    key={problem.id}
                    to={`/companies/${problem.companyId}/problems`}
                    className={
                      problem.status === 'closed' || problem.status === 'resolved' ? 'result-row is-done' : 'result-row'
                    }
                  >
                    <div className="row wrap" style={{ gap: 8 }}>
                      <span className="key">{problem.reference}</span>
                      <span className="grow" style={{ minWidth: 0 }}>
                        <Highlight text={problem.title} query={query} />
                      </span>
                      <span className="badge">{PROBLEM_STATUS_LABELS[problem.status]}</span>
                    </div>
                    {/*
                      The workaround on the result itself. Somebody searching "statements will
                      not download" wants the answer, not a link to go and find the answer.
                    */}
                    {problem.workaround && (
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        <span className="dim">Workaround: </span>
                        <Highlight text={problem.workaround} query={query} />
                      </div>
                    )}
                    <div className="row wrap dim" style={{ gap: 10, fontSize: 11, marginTop: 3 }}>
                      <span>{problem.companyName}</span>
                      <span>
                        {problem.incidentCount} incident{problem.incidentCount === 1 ? '' : 's'}
                      </span>
                      <PriorityBadge priority={problem.priority} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {results.changes.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h2>Changes</h2>
                <span className="dim" style={{ fontSize: 12 }}>
                  {results.changes.length}
                </span>
              </div>
              <div>
                {results.changes.map((change) => (
                  <Link
                    key={change.id}
                    to={`/projects/${change.project.id}/changes`}
                    className={
                      change.status === 'closed' || change.status === 'rolled_back'
                        ? 'result-row is-done'
                        : 'result-row'
                    }
                  >
                    <div className="row wrap" style={{ gap: 8 }}>
                      <span className="key">{change.reference}</span>
                      <span className="grow" style={{ minWidth: 0 }}>
                        <Highlight text={change.title} query={query} />
                      </span>
                      <span className="badge">{CHANGE_STATUS_LABELS[change.status]}</span>
                      {change.mergeIid && <span className="badge badge-accent">!{change.mergeIid}</span>}
                    </div>
                    <div className="row wrap dim" style={{ gap: 10, fontSize: 11, marginTop: 3 }}>
                      <span>
                        {change.companyName} · {change.project.name}
                      </span>
                      <span>{change.type}</span>
                      <span>{change.risk} risk</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {results.projects.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h2>Projects</h2>
              </div>
              <div>
                {results.projects.map((project) => (
                  <Link key={project.id} to={`/projects/${project.id}/board`} className="result-row">
                    <div className="row" style={{ gap: 8 }}>
                      <span className="key">{project.code}</span>
                      <Highlight text={project.name} query={query} />
                    </div>
                    <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>
                      {project.companyName}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {results.companies.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h2>Companies</h2>
              </div>
              <div>
                {results.companies.map((company) => (
                  <Link key={company.id} to={`/companies/${company.id}`} className="result-row">
                    <div className="row" style={{ gap: 8 }}>
                      <span className="key">{company.code}</span>
                      <Highlight text={company.name} query={query} />
                      <KindBadge kind={company.kind} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/** Marks the matched run, so it is obvious why a result is in the list. */
function Highlight({ text, query }: { text: string; query: string }) {
  const at = text.toLowerCase().indexOf(query.trim().toLowerCase());
  if (at === -1) return <>{text}</>;

  const end = at + query.trim().length;
  return (
    <>
      {text.slice(0, at)}
      <mark className="hit">{text.slice(at, end)}</mark>
      {text.slice(end)}
    </>
  );
}
